import { AsyncPipe } from '@angular/common'
import type { OnInit } from '@angular/core'
import { Component, DestroyRef, NgZone, inject, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { RouterLink } from '@angular/router'

import { TranslatePipe } from '@ngx-translate/core'
import { InfiniteScrollDirective } from 'ngx-infinite-scroll'
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs'
import { debounceTime, distinctUntilChanged } from 'rxjs/operators'

import { AuthorsService } from '../../../../../api/authors-service/authors.service'
import type { AuthorDto } from '../../../../../api/responses'
import type { AuthorGroup, SortOrder } from '../../../../../types/types'
import { AuthorItemComponent } from '../author-item/author-item.component'

const LOAD_AUTHORS_PER_PAGE = 150

@Component({
  selector: 'dhp-authors-list',
  templateUrl: './authors-list.component.html',
  styleUrls: ['./authors-list.component.scss'],
  imports: [
    InfiniteScrollDirective,
    RouterLink,
    AuthorItemComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class AuthorsListComponent implements OnInit {
  private readonly authorsService = inject(AuthorsService)
  private readonly ngZone = inject(NgZone)
  private readonly destroyRef = inject(DestroyRef)

  readonly groupedAuthors = signal<AuthorGroup[]>([])
  loading$ = new BehaviorSubject<boolean>(false)
  error: string | null = null
  currentPage = 1
  readonly totalItems = signal(0)
  readonly showSortDropdown = signal(false)
  private readonly authors = signal<AuthorDto[]>([])

  private readonly searchTerm$ = new BehaviorSubject<string | undefined>(
    undefined
  )
  private readonly sortOrder$ = new BehaviorSubject<SortOrder>('asc')
  private readonly loadedPages$ = new BehaviorSubject<number>(1)

  get hasMoreItems(): boolean {
    return this.totalItems() > this.loadedPages$.value * LOAD_AUTHORS_PER_PAGE
  }

  get searchTerm(): string | undefined {
    return this.searchTerm$.value
  }

  set searchTerm(term: string | undefined) {
    if (term?.trim().length === 0) {
      term = undefined
    }
    this.resetList()
    this.searchTerm$.next(term)
  }

  get sortOrder(): SortOrder {
    return this.sortOrder$.value
  }

  set sortOrder(sortOrder: SortOrder) {
    if (this.sortOrder !== sortOrder) {
      this.resetList()
    }
    this.sortOrder$.next(sortOrder)
  }

  ngOnInit(): void {
    combineLatest([
      this.searchTerm$.pipe(debounceTime(300)),
      this.sortOrder$,
      this.loadedPages$,
    ])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        startWith<[string | undefined, SortOrder, number]>([
          undefined,
          'asc',
          1,
        ]),
        distinctUntilChanged((prev, curr) => {
          return (
            prev[0] === curr[0] && prev[1] === curr[1] && prev[2] === curr[2]
          )
        }),
        switchMap(([searchTerm, sortOrder, loadedPages]) => {
          this.loading$.next(true)
          return this.authorsService.getAuthors(
            loadedPages,
            LOAD_AUTHORS_PER_PAGE,
            'last_name',
            sortOrder,
            {
              name: { like: searchTerm },
            }
          )
        }),
        tap(({ Authors: authorsContainer }) => {
          const { totalDocs, docs } = authorsContainer
          this.totalItems.set(totalDocs ?? 0)
          this.authors.set([...this.authors(), ...docs])
          this.groupedAuthors.set(this.groupAuthors(this.authors()))
          this.loading$.next(false)
        }),
        catchError((err: unknown) => {
          this.loading$.next(false)
          console.warn('failed to load authors', err)
          return of()
        })
      )
      .subscribe()
  }

  setSortOrder(order: SortOrder): void {
    if (this.sortOrder !== order) {
      this.sortOrder = order
    }
    this.showSortDropdown.set(false)
  }

  onScroll(): void {
    this.ngZone.run(() => {
      if (this.loading$.value) {
        return
      }

      if (!this.hasMoreItems) {
        return
      }

      this.loadMore()
    })
  }

  loadMore(): void {
    if (this.hasMoreItems) {
      this.loadedPages$.next(this.loadedPages$.value + 1)
    }
  }

  trackByAuthorId(index: number, author: AuthorDto): string {
    return author.id?.toString() ?? index.toString()
  }

  trackByLetter(index: number, group: AuthorGroup): string {
    return group.letter
  }

  toggleSortDropdown(): void {
    this.showSortDropdown.set(!this.showSortDropdown())
  }

  private resetList(): void {
    this.authors.set([])
    this.loadedPages$.next(1)
    this.groupedAuthors.set([])
    this.totalItems.set(0)
  }

  private groupAuthors(authors: AuthorDto[]): AuthorGroup[] {
    // First, sort the authors within each group based on last_name
    const sortedAuthors = [...authors].sort((a, b) => {
      const compareResult = (a.last_name ?? '').localeCompare(b.last_name ?? '')
      return this.sortOrder === 'asc' ? compareResult : -compareResult
    })

    // Group authors by first letter of last name
    const groups = sortedAuthors.reduce(
      (acc: Record<string, AuthorDto[]>, author) => {
        const letter = (author.last_name?.[0] ?? '#').toUpperCase()
        if (!acc[letter]) {
          acc[letter] = []
        }
        acc[letter].push(author)
        return acc
      },
      {}
    )

    // Always sort the group letters alphabetically (A-Z), regardless of sortOrder
    // This ensures the list always starts with 'A'
    return Object.entries(groups)
      .map(([letter, authors]) => ({ letter, authors }))
      .sort((a, b) =>
        this.sortOrder === 'asc'
          ? a.letter.localeCompare(b.letter)
          : b.letter.localeCompare(a.letter)
      )
  }
}
